import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      !window.location.pathname.includes("/login")
    ) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  getMe: () => api.get("/auth/me"),
};

export const usersAPI = {
  getAll: (params) => api.get("/users", { params }),
  update: (id, data) => api.put(`/users/${id}`, data),
  setActive: (id, isActive) => api.put(`/users/${id}/status`, { is_active: isActive }),
  heartbeat: () => api.post("/users/heartbeat"),
  getOnline: () => api.get("/users/online"),
  getStats: () => api.get("/users/me/stats"),
  changeMyPassword: (data) => api.put("/users/me/password", data),
};

export const whatsappAPI = {
  getStatus: () => api.get("/whatsapp/status"),
  connect: () => api.post("/whatsapp/connect"),
  disconnect: () => api.post("/whatsapp/disconnect"),
  sendMessage: (phone, message) =>
    api.post("/whatsapp/send", { phone, message }),
};

export const ticketsAPI = {
  getAll: (params) => api.get("/tickets", { params }),
  getById: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post("/tickets", data),
  update: (id, data) => api.put(`/tickets/${id}`, null, { params: data }),
  closePotential: (id) => api.post(`/tickets/${id}/close-potential`),
  getMessages: (id, params) => api.get(`/tickets/${id}/messages`, { params }),
  sendWhatsApp: (id, payload) =>
    api.post(`/tickets/${id}/send-whatsapp`, payload),
  editMessage: (msgId, data) => api.put(`/messages/${msgId}`, data),
  deleteMessage: (msgId) => api.delete(`/messages/${msgId}`),
  transfer: (id, targetUserId, reason) =>
    api.post(`/tickets/${id}/transfer`, {
      target_user_id: targetUserId,
      reason,
    }),
  linkClient: (id, clientData) =>
    api.post(`/tickets/${id}/link-client`, clientData),
  unlinkClient: (ticketId) => api.post(`/tickets/${ticketId}/unlink-client`),
  uploadFile: (
    id,
    file,
    quoted_message_id = null,
    quoted_message_text = null,
    quoted_from_me = false,
  ) => {
    const formData = new FormData();
    formData.append("file", file);

    if (quoted_message_id)
      formData.append("quoted_message_id", quoted_message_id);
    if (quoted_message_text)
      formData.append("quoted_message_text", quoted_message_text);

    formData.append("quoted_from_me", String(quoted_from_me));

    return api.post(`/tickets/${id}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  markAsRead: (id) => api.put(`/tickets/${id}/read`),
};

export const queueAPI = {
  getPending: () => api.get("/queue/pending"),
  takeTicket: (id) => api.post(`/queue/${id}/take`),
};

export const messagesAPI = {
  create: (data) => api.post("/messages", data),
};

export const categoriesAPI = {
  getAll: () => api.get("/categories"),
  create: (data) => api.post("/categories", data),
  delete: (id) => api.delete(`/categories/${id}`),
};

export const autoResponsesAPI = {
  getAll: () => api.get("/auto-responses"),
  create: (data) => api.post("/auto-responses", data),
  update: (id, data) =>
    api.put(`/auto-responses/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/auto-responses/${id}`),
};

export const configAPI = {
  get: () => api.get("/config"),
  update: (data) => api.put("/config", null, { params: data }),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/config/logo", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getHours: () => api.get("/config/hours"),
  updateHours: (data) => api.put("/config/hours", data),
};

export const welcomeQuestionsAPI = {
  getAll: () => api.get("/welcome-questions"),
  create: (data) => api.post("/welcome-questions", data),
  update: (id, data) =>
    api.put(`/welcome-questions/${id}`, null, { params: data }),
  delete: (id) => api.delete(`/welcome-questions/${id}`),
};

export const reportsAPI = {
  getTickets: (params) => api.get("/reports/tickets", { params }),
};

export const clientsAPI = {
  getAll: () => api.get("/clients"),
  getExternal: () => api.get("/external-clients"),
  updateLocal: (whatsappId, data) => api.put(`/clients/${whatsappId}`, data),
};

export const manualTicketAPI = {
  create: (data) => api.post("/tickets/manual", data),
};

export const internalChatAPI = {
  getUsers: () => api.get("/internal-chat/users"),
  getMessages: (userId) => api.get(`/internal-chat/${userId}/messages`),
  sendMessage: (receiverId, message, quotedMessageId = null) =>
    api.post("/internal-chat/send", { 
      receiver_id: receiverId, 
      message,
      quoted_message_id: quotedMessageId 
    }),
  getUnreadCounts: () => api.get("/internal-chat/unread-counts"),
  uploadAttachment: (receiverId, file, quotedMessageId = null) => {
    const formData = new FormData();
    formData.append("receiver_id", receiverId);
    formData.append("file", file);
    if (quotedMessageId) formData.append("quoted_message_id", quotedMessageId);
    return api.post("/internal-chat/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  editMessage: (msgId, data) => api.put(`/internal-chat/messages/${msgId}`, data),
  deleteMessage: (msgId) => api.delete(`/internal-chat/messages/${msgId}`),
};

export const dashboardAPI = {
  getAnalytics: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return api.get("/dashboard/analytics", { params });
  },
};

export const aiAPI = {
  evaluateMessage: (phone, message) =>
    api.post("/ai/evaluate-message", null, { params: { phone, message } }),
};

export const knowledgeAPI = {
  getAll: () => api.get('/knowledge'),
  create: (data) => api.post('/knowledge', data),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  delete: (id) => api.delete(`/knowledge/${id}`),
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/knowledge/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const procedureRecordingsAPI = {
  start: (ticketId, mimeType) =>
    api.post("/procedure-recordings", {
      ticket_id: ticketId,
      mime_type: mimeType,
    }),
  uploadChunk: (recordingId, chunkIndex, chunk) =>
    api.put(`/procedure-recordings/${recordingId}/chunks/${chunkIndex}`, chunk, {
      headers: { "Content-Type": "application/octet-stream" },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    }),
  complete: (recordingId, data) => {
    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("description", data.description || "");
    formData.append("duration_seconds", String(data.durationSeconds || 0));
    formData.append("shared_with_team", String(data.sharedWithTeam));
    formData.append("available_in_bot", String(data.availableInBot === true));
    if (data.thumbnail) formData.append("thumbnail", data.thumbnail, "thumbnail.webp");

    return api.post(`/procedure-recordings/${recordingId}/complete`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  },
  importToLibrary: (data, onUploadProgress) => {
    const formData = new FormData();
    formData.append("video_file", data.file);
    formData.append("title", data.title);
    formData.append("description", data.description || "");
    formData.append("duration_seconds", String(data.durationSeconds || 0));
    formData.append("shared_with_team", String(data.sharedWithTeam));
    formData.append("available_in_bot", String(data.availableInBot === true));

    return api.post("/procedure-recordings/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      timeout: 0,
      onUploadProgress,
    });
  },
  discardDraft: (recordingId) =>
    api.delete(`/procedure-recordings/${recordingId}/draft`),
  list: (params) => api.get("/procedure-recordings", { params }),
  count: (ticketId) =>
    api.get("/procedure-recordings/count", { params: { ticket_id: ticketId } }),
  get: (recordingId) => api.get(`/procedure-recordings/${recordingId}`),
  getVideo: (recordingId) =>
    api.get(`/procedure-recordings/${recordingId}/video`, {
      responseType: "blob",
    }),
  getThumbnail: (recordingId) =>
    api.get(`/procedure-recordings/${recordingId}/thumbnail`, {
      responseType: "blob",
    }),
  sendToTicket: (ticketId, recordingIds) =>
    api.post(`/tickets/${ticketId}/procedure-recordings/send`, {
      recording_ids: recordingIds,
    }),
  updateMetadata: (recordingId, data) =>
    api.put(`/procedure-recordings/${recordingId}/metadata`, data),
  remove: (recordingId) => api.delete(`/procedure-recordings/${recordingId}`),
};

export default api;
